import { useState } from 'react';

import { Card } from '../../components/ui/Card.js';
import { DataState } from '../../components/ui/DataState.js';
import { useInsightsUsage } from './api.js';

const DAY_OPTIONS = [7, 30, 90];

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/**
 * Admin observability for Insights Studio (rendered as a tab on the AI Assistant
 * settings page): tokens + estimated dollars per member per day, cache hit ratio,
 * top questions, failed turns.
 */
export function InsightsUsagePanel(): JSX.Element {
  const [days, setDays] = useState(30);
  const usageQuery = useInsightsUsage(days, true);
  const report = usageQuery.data;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {DAY_OPTIONS.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setDays(option)}
            className={`rounded-lg border px-3 py-1.5 text-xs font-medium ${
              days === option
                ? 'border-brand-300 bg-brand-50 text-brand-700'
                : 'border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            Last {option} days
          </button>
        ))}
      </div>

      <DataState
        isLoading={usageQuery.isLoading}
        isError={usageQuery.isError}
        isEmpty={!report}
      >
        {report ? (
          <>
            <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
              <Stat label="Turns" value={report.totals.turns.toLocaleString()} />
              <Stat label="Input tokens" value={report.totals.inputTokens.toLocaleString()} />
              <Stat label="Output tokens" value={report.totals.outputTokens.toLocaleString()} />
              <Stat
                label="Cache hit ratio"
                value={`${(report.totals.cacheHitRatio * 100).toFixed(1)}%`}
              />
              <Stat label="Est. cost" value={USD.format(report.totals.estCostUsd)} />
              <Stat
                label="Failed turns"
                value={report.totals.failedTurns.toLocaleString()}
                tone={report.totals.failedTurns > 0 ? 'warn' : undefined}
              />
            </div>

            <Card className="p-4">
              <h3 className="text-sm font-semibold text-slate-900">Per member per day</h3>
              <div className="mt-2 max-h-80 overflow-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-slate-500">
                      <th className="py-1.5 pr-3 font-medium">Date</th>
                      <th className="py-1.5 pr-3 font-medium">Member</th>
                      <th className="py-1.5 pr-3 text-right font-medium">Turns</th>
                      <th className="py-1.5 pr-3 text-right font-medium">In</th>
                      <th className="py-1.5 pr-3 text-right font-medium">Out</th>
                      <th className="py-1.5 pr-3 text-right font-medium">Cache read</th>
                      <th className="py-1.5 pr-3 text-right font-medium">Cache write</th>
                      <th className="py-1.5 text-right font-medium">Est. cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.perMemberDay.map((row) => (
                      <tr key={`${row.memberId}|${row.date}`} className="border-b border-slate-100">
                        <td className="whitespace-nowrap py-1.5 pr-3 text-slate-700">{row.date}</td>
                        <td className="max-w-44 truncate py-1.5 pr-3 text-slate-700">{row.memberName}</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums">{row.turns}</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums">{row.inputTokens.toLocaleString()}</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums">{row.outputTokens.toLocaleString()}</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums">{row.cacheReadTokens.toLocaleString()}</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums">{row.cacheWriteTokens.toLocaleString()}</td>
                        <td className="py-1.5 text-right tabular-nums">{USD.format(row.estCostUsd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {report.perMemberDay.length === 0 ? (
                  <p className="py-6 text-center text-sm text-slate-500">No usage in this window.</p>
                ) : null}
              </div>
              <p className="mt-2 text-[11px] text-slate-400">
                Costs are estimated from published per-token rates — check your Anthropic console for billing.
              </p>
            </Card>

            <div className="grid gap-4 lg:grid-cols-2">
              <Card className="p-4">
                <h3 className="text-sm font-semibold text-slate-900">Top questions</h3>
                {report.topQuestions.length === 0 ? (
                  <p className="mt-2 text-sm text-slate-500">No questions in this window.</p>
                ) : (
                  <ol className="mt-2 space-y-1.5 text-xs text-slate-700">
                    {report.topQuestions.map((q) => (
                      <li key={q.question} className="flex items-start justify-between gap-3">
                        <span className="min-w-0 flex-1">{q.question}</span>
                        <span className="shrink-0 tabular-nums text-slate-400">×{q.count}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </Card>

              <Card className="p-4">
                <h3 className="text-sm font-semibold text-slate-900">Failed turns</h3>
                {report.failedTurns.length === 0 ? (
                  <p className="mt-2 text-sm text-slate-500">No failed turns in this window.</p>
                ) : (
                  <ul className="mt-2 space-y-1.5 text-xs text-slate-700">
                    {report.failedTurns.map((row, i) => (
                      <li key={i} className="flex items-start justify-between gap-3">
                        <span className="min-w-0 flex-1 truncate" title={row.conversationTitle}>
                          {row.conversationTitle} — {row.memberName}
                        </span>
                        <span className="shrink-0 text-slate-400">
                          {row.stopReason ?? 'unknown'} · {row.createdAt.slice(0, 10)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </div>
          </>
        ) : null}
      </DataState>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'warn' }): JSX.Element {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
      <p className="text-[11px] font-medium text-slate-500">{label}</p>
      <p className={`mt-0.5 text-lg font-semibold ${tone === 'warn' ? 'text-rose-600' : 'text-slate-900'}`}>
        {value}
      </p>
    </div>
  );
}
